import { TileSize } from "game-common/models"
import { OrganicOre } from "game-common/room/cave/organic_cave"

/**
 * THE ORGANIC CAVE'S ROCK, DRAWN: one continuous basalt mass painted from which cells are still rock, instead
 * of a sprite per rock tile. Nothing here is looked up from an autotile table -- every shape the player can
 * dig is drawn by the same three rules, so a tunnel, a notch, a lone pillar and a breach all come out right:
 *
 *  - FOOTPRINT: a cell-centred field (rock = 1, open = 0) blended smoothly across each cell and cut at one
 *    half, with a world-space wobble, so a straight run sits on the cell line, corners round off and the
 *    edge never looks ruled. It is the collision square give or take a few pixels.
 *  - FACE: every footprint pixel within FACE_H of the rock's south edge (the side the camera sees) is wall
 *    face; the rest is the rock's top. The face sits INSIDE the rock's own footprint with its foot on the
 *    collision edge, so a body walking north stops at its foot and stands in front of it.
 *  - OVERHANG: the top is seen from above a wall FACE_H tall, so it reaches past the collision line on the
 *    north side, over the floor; a body standing there passes under it (paintRockLayers' rims, sorted with
 *    bodies by the layer), which is what makes a wall to your south read as a wall.
 *  - TEXTURE: basalt columns -- a jittered hex lattice seen from above on the top, the same columns' sides
 *    on the face (a face pixel takes the column at its foot, so faces and the tops above them agree).
 *
 * Every variation is keyed on WORLD position, so digging only ever reveals: a swing re-shapes the outline in
 * the few cells round it and leaves everything already on screen exactly as it was.
 *
 * Live tuning: window.rockMass.enabled / .faceHeight / .overhang, or window.rockMass = false.
 */
export const ROCK_MASS = { enabled: true, faceHeight: 52, overhang: 16 }

export const rockMassConfig = (): typeof ROCK_MASS => {
    if (typeof window === "undefined") return ROCK_MASS
    const w = window as any
    if (w.rockMass === undefined) w.rockMass = ROCK_MASS
    const live = w.rockMass
    if (live === false) return { ...ROCK_MASS, enabled: false }
    return live && typeof live === "object" ? live : ROCK_MASS
}

/** Pixels per side of one baked piece (4x4 tiles). The column texture repeats exactly once per piece. */
export const ROCK_CHUNK = 128
/** Rock read round anything baked: a face looks FACE_H below itself, the footprint ~16px past its cell. */
export const ROCK_MARGIN = 64

/** What the painter needs to know about the cave, cell by cell. Off the map is solid rock. */
export interface RockView {
    /** Rock standing now: unmined rock, or bedrock. */
    solid(cx: number, cy: number): boolean
    /** The mineral in the cell's rock, if any (whether or not it has been mined out since). */
    ore(cx: number, cy: number): OrganicOre | undefined
    /** Open now but rock when the cave was cut: the player's own tunnels, strewn with what they broke. */
    dug(cx: number, cy: number): boolean
    /** Floor of a pocket that was sealed in the rock: damp, worn smooth. */
    pocket(cx: number, cy: number): boolean
    /** 0..1, how broken a standing cell is. */
    damage(cx: number, cy: number): number
    /** Distance in world px from a point to the nearest `material` vein's line (Infinity when there is none). */
    veinDistance(px: number, py: number, material: OrganicOre): number
    /** One of the way home's two face cells: an opening is drawn in the face. */
    mouth?(cx: number, cy: number): boolean
    /** Still pools on the floor, in world px (centre and radii). */
    pools?(): ReadonlyArray<{ x: number; y: number; rx: number; ry: number }>
    /** Lava on the floor here: the rock leaves the cell's floor alone (no rubble, shadow or damp over it). */
    lava?(cx: number, cy: number): boolean
}

// ---------------------------------------------------------------- palette (the testore face's own colours)
const hex = (s: string): [number, number, number] =>
    [parseInt(s.slice(1, 3), 16), parseInt(s.slice(3, 5), 16), parseInt(s.slice(5, 7), 16)]
/** Basalt ramp, darkest first: #161215 #2b262a #3e373d #5e5252 #7e7068 #867e7f stretched to nine steps. */
export const BASALT = ["#110e11", "#1f1a1e", "#2b262a", "#363036", "#433b40", "#524749", "#635655", "#766762", "#8a7e7c"].map(hex)
/** Each ore's lump shades (dark .. glint) and the stain it leaves in the rock round it, after ore.png's sheet. */
const ORE: Record<OrganicOre, { lump: number[][]; stain: number[] }> = {
    coal: { lump: ["#0b090b", "#1c181b", "#2e282c", "#5b5257", "#a9a1a6"].map(hex), stain: hex("#120e10") },
    iron: { lump: ["#5a2410", "#9a4519", "#cf6a26", "#f3a24f", "#ffd690"].map(hex), stain: hex("#4b2216") },
    copper: { lump: ["#3b2010", "#7a3f1d", "#b8662e", "#e39a55", "#7fd6b0"].map(hex), stain: hex("#3a2416") },
    silver: { lump: ["#2a2c33", "#5d6370", "#9aa2b2", "#d6dde8", "#ffffff"].map(hex), stain: hex("#2a2e38") },
    gold: { lump: ["#4a2f06", "#94660f", "#d39f25", "#f6d35a", "#fff3b8"].map(hex), stain: hex("#3f2a08") },
}
const POOL = ["#0f1a24", "#203646", "#4f7889"].map(hex)

// ---------------------------------------------------------------- hash / noise
const hash = (x: number, y: number, seed: number): number => {
    let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ seed
    h = Math.imul(h ^ h >>> 13, 1274126177)
    return ((h ^ h >>> 16) >>> 0) / 4294967295
}
const smooth = (v: number) => v * v * (3 - 2 * v)
const valueNoise = (x: number, y: number, seed: number): number => {
    const ix = Math.floor(x), iy = Math.floor(y)
    const sx = smooth(x - ix), sy = smooth(y - iy)
    const a = hash(ix, iy, seed), b = hash(ix + 1, iy, seed), c = hash(ix, iy + 1, seed), d = hash(ix + 1, iy + 1, seed)
    const top = a + (b - a) * sx
    return top + ((c + (d - c) * sx) - top) * sy
}
const mod = (v: number, m: number) => ((v % m) + m) % m

// ---------------------------------------------------------------- the column texture, one repeating tile
/** Columns across and rows down one ROCK_CHUNK: the jittered lattice repeats exactly once per piece. */
const COLS = 9, ROWS = 12
const COL_W = ROCK_CHUNK / COLS, ROW_H = ROCK_CHUNK / ROWS

interface ColumnTile {
    /** Which column (0..COLS*ROWS-1) each pixel of the repeating tile belongs to. */
    id: Uint8Array
    /** The column top's palette index at each pixel, seen from above. */
    top: Uint8Array
}

let columnTile: ColumnTile | undefined
/** The basalt columns, worked out once: every top and face pixel after this is a table lookup. */
export const columns = (): ColumnTile => {
    if (columnTile) return columnTile
    const S = ROCK_CHUNK
    const id = new Uint8Array(S * S), top = new Uint8Array(S * S)
    const point = (c: number, r: number): [number, number] => {
        const pc = mod(c, COLS), pr = mod(r, ROWS)
        return [c * COL_W + (r & 1) * COL_W / 2 + (hash(pc, pr, 11) - 0.5) * 5, r * ROW_H + (hash(pc, pr, 23) - 0.5) * 4]
    }
    for (let y = 0; y < S; y++) {
        const r0 = Math.floor(y / ROW_H)
        for (let x = 0; x < S; x++) {
            let d1 = 1e9, d2 = 1e9, best = 0, bx = 0, by = 0
            for (let dr = -1; dr <= 1; dr++) {
                const r = r0 + dr
                const c0 = Math.floor((x - (r & 1) * COL_W / 2) / COL_W)
                for (let dc = -1; dc <= 1; dc++) {
                    const c = c0 + dc
                    const [px, py] = point(c, r)
                    const d = Math.hypot(x - px, y - py)
                    if (d < d1) { d2 = d1; d1 = d; best = mod(r, ROWS) * COLS + mod(c, COLS); bx = px; by = py } else if (d < d2) d2 = d
                }
            }
            const i = y * S + x
            id[i] = best
            const tr = hash(best, 0, 31)
            const tone = tr < 0.2 ? -1 : tr > 0.8 ? 1 : 0
            const near = d2 - d1 < 2.6
            const upLeft = (x - bx) + (y - by) < 0
            top[i] = d2 - d1 < 1.1 ? 1 : 3 + tone + (near && upLeft ? 1 : 0)
        }
    }
    return (columnTile = { id, top })
}
const columnAt = (tile: ColumnTile, px: number, py: number) => tile.id[mod(py, ROCK_CHUNK) * ROCK_CHUNK + mod(px, ROCK_CHUNK)]
const topAt = (tile: ColumnTile, px: number, py: number) => tile.top[mod(py, ROCK_CHUNK) * ROCK_CHUNK + mod(px, ROCK_CHUNK)]

// ---------------------------------------------------------------- footprint
/**
 * 1 per world pixel of the region (x0, y0, w, h) that is rock. Each cell's solidity sits at its CENTRE and is
 * blended smoothly to its neighbours', cut at one half: a run between a rock and an open cell lands on the
 * line between them, and the wobble (long and fine) moves it a few pixels either way.
 */
export const rockMask = (view: RockView, x0: number, y0: number, w: number, h: number): Uint8Array => {
    const mask = new Uint8Array(w * h)
    const cx0 = Math.floor((x0 + 0.5) / TileSize - 0.5) - 1, cy0 = Math.floor((y0 + 0.5) / TileSize - 0.5) - 1
    const cw = Math.ceil(w / TileSize) + 3, ch = Math.ceil(h / TileSize) + 3
    const solid = new Uint8Array(cw * ch)
    for (let j = 0; j < ch; j++) for (let i = 0; i < cw; i++) solid[j * cw + i] = view.solid(cx0 + i, cy0 + j) ? 1 : 0
    for (let y = 0; y < h; y++) {
        const py = y0 + y, v = (py + 0.5) / TileSize - 0.5, j0 = Math.floor(v), sv = smooth(v - j0)
        const row = (j0 - cy0) * cw
        for (let x = 0; x < w; x++) {
            const px = x0 + x, u = (px + 0.5) / TileSize - 0.5, i0 = Math.floor(u), su = smooth(u - i0)
            const k = row + i0 - cx0
            const a = solid[k], b = solid[k + 1], c = solid[k + cw], d = solid[k + cw + 1]
            if (a + b + c + d === 4) { mask[y * w + x] = 1; continue }
            if (a + b + c + d === 0) continue
            const f = (a + (b - a) * su) * (1 - sv) + (c + (d - c) * su) * sv +
                0.40 * (valueNoise(px / 21, py / 21, 7) - 0.5) + 0.14 * (valueNoise(px / 6.5, py / 6.5, 9) - 0.5)
            if (f > 0.5) mask[y * w + x] = 1
        }
    }
    return mask
}

// ---------------------------------------------------------------- the painter
/**
 * The rock's art over the region (x0, y0, w, h) of the world, which must include `margin` on every side.
 * Returns RGBA for the INTERIOR (the region less the margin): rock pixels opaque, the floor transparent
 * except where the rock marks it (contact shadow, rubble, a pocket's damp), so the floor tiles show through.
 */
export const paintRock = (view: RockView, x0: number, y0: number, w: number, h: number, margin: number,
    faceHeight: number = ROCK_MASS.faceHeight): Uint8ClampedArray => paintRockLayers(view, x0, y0, w, h, margin, faceHeight).base

/**
 * How far into the rock, from its NORTH rim, the mass is drawn in front of a body standing north of it: a body's
 * sprite reaches ~20px south of the point it sorts and collides by.
 */
export const ROCK_RIM_OVERLAY = 22
/** Shade down the east and west edges of a top, the LPC platform's hint of a side. */
const SIDE_SHADE = 2

export interface RockLayers {
    /** Everything, drawn below every body. */
    base: Uint8ClampedArray
    /**
     * The TOP within (overhang + ROCK_RIM_OVERLAY) of its north edge: drawn again in front of bodies, row band by
     * row band, sorted as if each row stood `overhang` px further south (where its rock really is).
     */
    topRim: Uint8ClampedArray
    /** A thin wall's face within ROCK_RIM_OVERLAY of its north edge: in front of a body behind the wall only. */
    faceRim: Uint8ClampedArray
}

/**
 * paintRock's art, split for depth: the base layer plus the two rims the layer sorts against bodies.
 *
 * THE OVERHANG. A wall is a block FACE_H tall. Its face is the south FACE_H px of its own footprint (the foot on
 * the collision line, so a body walking north stops at it and stands in front of it), and its top is seen from
 * above, lifted -- so the top reaches `overhang` px north PAST the collision line, over the floor. A body
 * standing north of a wall passes under that edge: the wall hides its feet and legs, which is the cue that we
 * are looking down over the wall at it. (Without it, a wall to your south was a flat pattern you stood on.)
 */
export const paintRockLayers = (view: RockView, x0: number, y0: number, w: number, h: number, margin: number,
    faceHeight: number = ROCK_MASS.faceHeight, overhang: number = ROCK_MASS.overhang): RockLayers => {
    const H = Math.max(2, Math.round(faceHeight))
    const L = Math.max(0, Math.round(overhang))
    const n = w * h
    const mask = rockMask(view, x0, y0, w, h)
    const tile = columns()

    // zt: rock pixels between this one and the footprint's south edge in its column (0 = its bottom row).
    // zn: the same from the north edge. zs: for floor, how far down the next rock starts (the overhang's reach).
    const zt = new Int32Array(n).fill(-1), zn = new Int32Array(n).fill(-1), zs = new Int32Array(n).fill(0)
    for (let x = 0; x < w; x++) {
        let run = 0, gap = 1 << 20
        for (let y = h - 1; y >= 0; y--) {
            const i = y * w + x
            if (mask[i]) { zt[i] = run++; gap = 0 } else { run = 0; gap++; zs[i] = gap }
        }
        run = 0
        for (let y = 0; y < h; y++) {
            const i = y * w + x
            if (mask[i]) zn[i] = run++
            else run = 0
        }
    }
    const isFace = (i: number) => zt[i] >= 0 && zt[i] < H
    const over = (i: number) => zt[i] < 0 && zs[i] <= L
    /** Drawn as rock: the footprint, and the top's overhang north of it. */
    const solid = (i: number) => zt[i] >= 0 || over(i)
    const isTop = (i: number) => zt[i] >= H || over(i)
    // zv: rock pixels (as drawn) above this one in its column, overhang included (0 = the drawn north edge).
    const zv = new Int32Array(n).fill(-1)
    for (let x = 0; x < w; x++) {
        let run = 0
        for (let y = 0; y < h; y++) {
            const i = y * w + x
            if (solid(i)) zv[i] = run++
            else run = 0
        }
    }
    // How far a top pixel is from the nearest floor or face (3-4 chamfer, in px): the top recedes into the
    // dark away from its edges, so the mass reads as a solid ridge and not as paving round a hole.
    const edge = new Float32Array(n)
    for (let i = 0; i < n; i++) edge[i] = isTop(i) ? 1e9 : 0
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!edge[i]) continue
        let d = edge[i]
        if (x > 0) d = Math.min(d, edge[i - 1] + 1)
        if (y > 0) d = Math.min(d, edge[i - w] + 1)
        if (x > 0 && y > 0) d = Math.min(d, edge[i - w - 1] + 1.4)
        if (x < w - 1 && y > 0) d = Math.min(d, edge[i - w + 1] + 1.4)
        edge[i] = d
    }
    for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
        const i = y * w + x
        if (!edge[i]) continue
        let d = edge[i]
        if (x < w - 1) d = Math.min(d, edge[i + 1] + 1)
        if (y < h - 1) d = Math.min(d, edge[i + w] + 1)
        if (x < w - 1 && y < h - 1) d = Math.min(d, edge[i + w + 1] + 1.4)
        if (x > 0 && y < h - 1) d = Math.min(d, edge[i + w - 1] + 1.4)
        edge[i] = d
    }

    const iw = w - 2 * margin, ih = h - 2 * margin
    const size = Math.max(0, iw * ih * 4)
    const base = new Uint8ClampedArray(size), topRim = new Uint8ClampedArray(size), faceRim = new Uint8ClampedArray(size)
    let copy: Uint8ClampedArray | undefined
    const set = (o: number, c: readonly number[], a = 255) => {
        base[o] = c[0]; base[o + 1] = c[1]; base[o + 2] = c[2]; base[o + 3] = a
        if (copy) { copy[o] = c[0]; copy[o + 1] = c[1]; copy[o + 2] = c[2]; copy[o + 3] = a }
    }

    for (let y = margin; y < h - margin; y++) {
        const py = y0 + y
        for (let x = margin; x < w - margin; x++) {
            const px = x0 + x
            const i = y * w + x
            const o = ((y - margin) * iw + (x - margin)) * 4
            if (!solid(i)) { copy = undefined; paintFloor(view, base, o, solid, zt, w, x, y, px, py, H); continue }

            const face = isFace(i)
            copy = face ? (zn[i] < ROCK_RIM_OVERLAY ? faceRim : undefined) : (zv[i] < L + ROCK_RIM_OVERLAY ? topRim : undefined)
            // Outline: any drawn rock pixel touching floor; the lit rim: a top pixel right above a face.
            if (!solid(i - 1) || !solid(i + 1) || !solid(i - w) || !solid(i + w)) { set(o, BASALT[0]); continue }
            if (!face && isFace(i + w)) { set(o, BASALT[8]); continue }

            // The rock this pixel belongs to: a face's own cell, an overhang's the rock below it.
            const rockY = face ? py + zt[i] - 1 : zt[i] < 0 ? py + zs[i] : py
            const cellX = Math.floor(px / TileSize), cellY = Math.floor(rockY / TileSize)
            let colour: number[]
            if (face) {
                const z = zt[i], footY = py + z
                const id = columnAt(tile, px, footY)
                const left = columnAt(tile, px - 1, footY), right = columnAt(tile, px + 1, footY)
                let idx = 3 + Math.floor(hash(id, 0, 31) * 3) - 1
                const hz = z / (H - 1)
                idx += hz < 0.2 ? -1 : hz > 0.74 ? 1 : 0
                if (left !== id && hash(id, 3, 57) < 0.55) idx += 1   // only some facets catch the light
                if (right !== id) idx -= 1
                if (hash(id, px, 41) < 0.12 && hash(id, Math.floor(z / 4), 43) < 0.6) idx -= 1
                for (let j = 0; j < 3; j++) {                          // cross joints: columns break into drums
                    if (hash(id, 20 + j, 53) >= 0.6) continue
                    const jz = Math.floor(hash(id, 10 + j, 51) * (H - 6)) + 3
                    const tilt = Math.floor(mod(px, 4) * (hash(id, 30 + j, 59) - 0.5))
                    if (z === jz + tilt) idx = 1
                    else if (z === jz + tilt - 1) idx += 1
                }
                if (hash(px, py, 91) < 0.035) idx -= 2                 // pits and chips
                if (z <= 2) idx = Math.min(idx, 2)                     // grime at the foot
                if (z === H - 1) idx = 7                               // the lip
                if (z === 0) idx = 1
                colour = BASALT[Math.max(0, Math.min(8, idx))].slice()
                const opening = mouthOpening(view, cellX, Math.floor((footY - 1) / TileSize), px, z, H)
                if (opening >= 0) { set(o, opening === 0 ? MOUTH_DARK : opening === 1 ? BASALT[0] : BASALT[6]); continue }
            } else {
                const recede = edge[i] >= 26 ? 2 : edge[i] >= 14 ? 1 : 0
                let idx = topAt(tile, px, py + H) - recede
                if (zv[i] === 1) idx = 6                               // the top's north edge, lit
                else if (!solid(i - SIDE_SHADE) || !solid(i + SIDE_SHADE)) idx -= 1
                colour = BASALT[Math.max(1, idx)].slice()
            }

            // Ore where the rock is exposed: a face, the top just above one, or the top's north edge.
            const lip = !face && (isFace(i + 2 * w) || isFace(i + 4 * w) || isFace(i + 6 * w))
            const material = (face || lip || (!face && zv[i] <= 4)) ? view.ore(cellX, cellY) : undefined
            if (material) {
                const { lump: shades, stain } = ORE[material]
                const dens = Math.pow(Math.max(0, 1 - view.veinDistance(px, rockY, material) / 24), 0.7)
                if (dens > 0.05) {
                    const k = 0.2 * (0.5 + dens)
                    for (let c = 0; c < 3; c++) colour[c] = colour[c] * 0.8 + stain[c] * k
                    const lump = oreLump(px, py, dens)
                    if (lump === -1) colour = [BASALT[0][0] * 0.9 + stain[0] * 0.1, BASALT[0][1] * 0.9 + stain[1] * 0.1, BASALT[0][2] * 0.9 + stain[2] * 0.1]
                    else if (lump >= 0) colour = shades[lump].slice()
                }
            }

            // Cracks spread through a cell as it is struck.
            const damage = view.damage(cellX, cellY)
            if (damage > 0 && crackAt(px, py, damage)) colour = BASALT[0].slice()
            set(o, colour)
        }
    }
    return { base, topRim, faceRim }
}

const MOUTH_DARK = [11, 8, 10]
/**
 * The way home, drawn into the face of its two mouth cells: an arch-topped opening centred on the pair.
 * -1 not in it, 0 the dark inside, 1 its outline, 2 the lit edge of the arch above the outline.
 */
const mouthOpening = (view: RockView, cx: number, cy: number, px: number, z: number, H: number): number => {
    if (!view.mouth?.(cx, cy)) return -1
    const left = view.mouth(cx - 1, cy) ? cx - 1 : cx
    const centre = (left + 1) * TileSize
    const top = Math.min(H - 6, 42)
    const halfWidth = (h: number) => h >= top ? -1 : 19 * Math.sqrt(1 - (h / top) * (h / top))
    const d = Math.abs(px + 0.5 - centre)
    const hw = halfWidth(z)
    if (d < hw - 1) return 0
    if (d < hw) return 1
    if (d < halfWidth(z - 1) + 0.5 && z - 1 < top) return 1
    if (d < halfWidth(z - 2) + 0.5 && z - 2 < top) return 2
    return -1
}

/** -2 none, -1 the dark ring round a lump, 0..4 the lump's ORE shade. Two staggered lattices so lumps cluster. */
const oreLump = (px: number, py: number, dens: number): number => {
    for (const off of [0, 3]) {
        const lx = Math.floor((px + off) / 7), ly = Math.floor((py + off) / 6)
        if (hash(lx, ly, 71 + off) >= dens * 0.75) continue
        const bx = lx * 7 - off + 2 + Math.floor(hash(lx, ly, 61 + off) * 3)
        const by = ly * 6 - off + 1 + Math.floor(hash(lx, ly, 67 + off) * 3)
        const dx = px - bx, dy = py - by
        const body = (dy === 0 && dx >= -1 && dx <= 1) || (dy === 1 && dx >= -1 && dx <= 0)
        if (body) {
            if (dy === 0 && dx === -1) return hash(lx, ly, 79) < 0.2 ? 4 : 3
            return dy === 0 ? 2 : 1
        }
        const ring = dx >= -2 && dx <= 2 && dy >= -1 && dy <= 2 &&
            !(dx === 2 && dy === 2) && !(dx === -2 && dy === -1) && !(dx === 2 && dy === -1) &&
            !(dx === 2 && dy === 1) && !(dx === 1 && dy === 2)
        if (ring) return -1
    }
    return -2
}

/** A crack network on a 6px jittered lattice; more of its edges show the more the cell is broken. */
const crackAt = (px: number, py: number, damage: number): boolean => {
    const S = 6
    const gx = Math.floor(px / S), gy = Math.floor(py / S)
    let d1 = 1e9, d2 = 1e9, a = 0, b = 0
    for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
        const cx = gx + i, cy = gy + j
        const qx = (cx + 0.5 + (hash(cx, cy, 101) - 0.5) * 0.8) * S, qy = (cy + 0.5 + (hash(cx, cy, 103) - 0.5) * 0.8) * S
        const d = Math.hypot(px - qx, py - qy)
        const id = cx * 7919 + cy
        if (d < d1) { d2 = d1; b = a; d1 = d; a = id } else if (d < d2) { d2 = d; b = id }
    }
    if (d2 - d1 >= 0.9) return false
    return hash(Math.min(a, b), Math.max(a, b), 107) < damage * 0.9
}

/** Floor pixels the rock marks: contact shadow under a face, rubble in dug cells, a pocket's damp. */
const paintFloor = (view: RockView, rgba: Uint8ClampedArray, o: number, solid: (i: number) => boolean, zt: Int32Array, w: number,
    x: number, y: number, px: number, py: number, H: number): void => {
    if (view.lava?.(Math.floor(px / TileSize), Math.floor(py / TileSize))) return
    let alpha = 0
    let colour: readonly number[] = [0, 0, 0]
    // Contact shadow: darkest right under the foot, gone four pixels down.
    const SHADOW = [0.72, 0.48, 0.26, 0.10]
    for (let k = 1; k <= 4; k++) {
        const j = (y - k) * w + x
        if (y - k >= 0 && zt[j] >= 0 && zt[j] < H) { alpha = SHADOW[k - 1]; break }
    }
    for (const pool of view.pools?.() ?? []) {
        const e = ((px + 0.5 - pool.x) / pool.rx) ** 2 + ((py + 0.5 - pool.y) / pool.ry) ** 2
        if (e < 1) {
            const glint = Math.abs(py + 0.5 - (pool.y - pool.ry * 0.35)) < 0.6 && Math.abs(px + 0.5 - (pool.x - pool.rx * 0.25)) < pool.rx * 0.33
            const c = glint ? POOL[2] : e > 0.72 ? POOL[1] : POOL[0]
            rgba[o] = c[0]; rgba[o + 1] = c[1]; rgba[o + 2] = c[2]; rgba[o + 3] = 255
            return
        }
        if (e < 1.35) alpha = Math.max(alpha, 0.3)
    }
    // The floor darkens toward the foot of every wall, deepest at the far wall's base (rock to the south of it),
    // so the floor reads as sunk between walls rather than painted round them.
    const AO_SOUTH = [0.5, 0.36, 0.25, 0.16, 0.09, 0.04], AO_SIDE = [0.34, 0.2, 0.1, 0.04]
    const rows = zt.length / w
    for (let k = 1; k <= AO_SOUTH.length && y + k < rows; k++) {
        if (solid((y + k) * w + x)) { alpha = Math.max(alpha, AO_SOUTH[k - 1]); break }
    }
    for (let k = 1; k <= AO_SIDE.length; k++) {
        if ((x - k >= 0 && solid(y * w + x - k)) || (x + k < w && solid(y * w + x + k))) { alpha = Math.max(alpha, AO_SIDE[k - 1]); break }
    }
    const cx = Math.floor(px / TileSize), cy = Math.floor(py / TileSize)
    if (view.pocket(cx, cy)) {
        colour = [0, 8, 26]
        alpha = Math.max(alpha, 0.22)
    }
    if (view.dug(cx, cy)) {
        let nearFace = false
        for (let k = 1; k <= 8 && !nearFace; k++) {
            const j = (y - k) * w + x
            if (y - k >= 0 && zt[j] >= 0 && zt[j] < H) nearFace = true
        }
        const rx = Math.floor(px / 5), ry = Math.floor(py / 5)
        if (hash(rx, ry, 87) < (nearFace ? 0.75 : 0.22)) {
            const big = hash(rx, ry, 89) < 0.3
            const dx = px - (rx * 5 + Math.floor(hash(rx, ry, 81) * 4)), dy = py - (ry * 5 + Math.floor(hash(rx, ry, 83) * 4))
            const wMax = big ? 2 : 1, hMax = big ? 1 : 0
            if (dx >= 0 && dy >= 0 && dx <= wMax && dy <= hMax) {
                const c = dx === 0 && dy === 0 ? BASALT[6] : BASALT[4]
                rgba[o] = c[0]; rgba[o + 1] = c[1]; rgba[o + 2] = c[2]; rgba[o + 3] = 255
                return
            }
            if (dy === hMax + 1 && dx >= 1 && dx <= wMax) { colour = [0, 0, 0]; alpha = Math.max(alpha, 0.4) }
        }
    }
    if (alpha <= 0) return
    rgba[o] = colour[0]; rgba[o + 1] = colour[1]; rgba[o + 2] = colour[2]; rgba[o + 3] = Math.round(alpha * 255)
}

/**
 * The world pixels a change to cell (cx, cy) can repaint: the footprint moves within a cell of its centre
 * (plus wobble), a face is decided by the rock up to FACE_H below it, and the shadow, rim, lip and rubble
 * reach a few pixels further.
 */
export const rockDirtyRect = (cx: number, cy: number, faceHeight: number = ROCK_MASS.faceHeight) => {
    const reach = TileSize / 2 + 10
    const x = cx * TileSize - reach, y = cy * TileSize - reach - Math.round(faceHeight) - 8
    // Below the cell, a new north edge moves the rims (overhang + ROCK_RIM_OVERLAY) and the top's lit edge down.
    return { x, y, w: TileSize + 2 * reach, h: TileSize + 2 * reach + Math.round(faceHeight) + 8 + ROCK_MASS.overhang + ROCK_RIM_OVERLAY + 6 }
}
